import { useRef, useState } from "react";
import { GripVertical, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "../../../lib/api";
import { Button } from "../../ui/button";
import { UploadPicker } from "../../UploadPicker";

const SLOTS = ["Front cover", "Back cover", "Spine (optional)"];
const pill = (on) => `text-xs px-3 py-1.5 rounded-lg border ${on ? "border-amber-500 text-amber-300" : "border-slate-700 text-slate-400"}`;

export function ImageSlots({ book, edition, items, setItems, placement, setPlacement }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null);
  const add = (list) => setItems((cur) => [...cur, ...list].slice(0, 3));
  const uploadMany = async (files) => {
    const list = Array.from(files || []).slice(0, 3 - items.length);
    if (!list.length) return;
    setBusy(true);
    const done = [];
    try {
      for (const f of list) { const fd = new FormData(); fd.append("file", f); fd.append("kind", "artwork"); const { data } = await api.post(`/books/${book.id}/uploads`, fd); done.push({ id: data.id, name: f.name }); }
      toast.success(`${done.length} image${done.length === 1 ? "" : "s"} added`);
    } catch (e) { toast.error(errMsg(e)); } finally { add(done); setBusy(false); ref.current.value = ""; }
  };
  const move = (from, to) => setItems((cur) => { const n = [...cur]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n; });
  return (
    <div className="space-y-2" data-testid={`${edition}-image-slots`}>
      <p className="text-[11px] text-slate-400">Drag to reorder: 1st image = front, 2nd = back, 3rd = spine. A 2" × 1.2" barcode area is kept clear on the back.</p>
      <ol className="space-y-1.5">
        {items.map((it, i) => (
          <li key={it.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag !== null && drag !== i) move(drag, i); setDrag(null); }}
            className={`flex items-center gap-2 border rounded-lg px-2 py-1.5 bg-slate-900/60 cursor-grab ${drag === i ? "border-amber-500" : "border-slate-800"}`} data-testid={`${edition}-slot-${i}`}>
            <GripVertical size={14} className="text-slate-500 shrink-0" />
            <img src={`${API}/uploads/${it.id}/thumb`} alt="" className="w-9 h-9 rounded object-cover bg-slate-800 shrink-0" />
            <div className="min-w-0 flex-1"><p className="text-[11px] text-amber-300">{SLOTS[i]}</p><p className="text-xs text-slate-200 truncate">{it.name}</p></div>
            {i > 0 && <button type="button" onClick={() => move(i, i - 1)} className="text-[11px] text-slate-400 hover:text-amber-300" data-testid={`${edition}-slot-up-${i}`}>↑</button>}
            <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400" data-testid={`${edition}-slot-remove-${i}`}><X size={13} /></button>
          </li>
        ))}
      </ol>
      {items.length < 3 && (
        <div className="flex flex-wrap gap-2">
          <input ref={ref} type="file" multiple accept=".png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadMany(e.target.files)} data-testid={`${edition}-slots-file`} />
          <Button size="sm" variant="outline" className="border-slate-700" disabled={busy} onClick={() => ref.current.click()} data-testid={`${edition}-slots-upload`}><Upload size={13} className="mr-1" />{busy ? "Uploading…" : "Upload images"}</Button>
          <UploadPicker kind="image" label="Pick from my uploads" testId={`${edition}-slots-pick`} onPick={async (u) => add([{ id: u.id, name: u.filename }])} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center"><span className="text-[11px] text-slate-400">Placement</span>
        {[["fill", "Full-bleed, edge to edge"], ["fit", "Fit inside margins"]].map(([k, l]) => <button key={k} type="button" onClick={() => setPlacement(k)} className={pill(placement === k)} data-testid={`${edition}-placement-${k}`}>{l}</button>)}
      </div>
    </div>
  );
}
